// The shared US-address value (BRK-S067) — the single shape every address input
// in the portal is controlled by (AccountForm legal + mailing, BrokerageCard).
// Kept out of the component file so the component module exports only a
// component (react-refresh) and so the Places client can share the type without
// importing UI.

export interface AddressValue {
  street: string;
  city: string;
  state: string;
  zip: string;
  /** The county, bare as PMS stores it: "Cook", not "Cook County" (BRK-S308).
   *  Optional because only the commercial account addresses ask for it
   *  (`AddressFieldset withCounty`); the surety and bond editors never carry
   *  one, so their values keep exactly the four parts they always had. */
  county?: string;
  /** A unit, suite or floor, as PMS's `addressLine2` (BRK-S302). Optional for
   *  the same reason: only the WC wizard's locations ask for it
   *  (`AddressFieldset withLine2`). */
  addressLine2?: string;
}

/** A blank address — the shared empty value for create forms. */
export const EMPTY_ADDRESS: AddressValue = {
  street: "",
  city: "",
  state: "",
  zip: "",
};

/**
 * Whether all four parts are filled in.
 *
 * The contract has no half-address: PMS 400s a legal address missing a member,
 * so a partial write OMITS the whole block rather than sending three quarters of
 * one. Lived in `AccountForm` until BRK-S176 gave the Surety profile an address
 * editor of its own — two forms deciding separately what "complete" means is two
 * answers to a question the server only has one of.
 */
export function isCompleteAddress(a: AddressValue): boolean {
  return Boolean(
    a.street.trim() && a.city.trim() && a.state.trim() && a.zip.trim(),
  );
}

/** The same address with every part trimmed — what actually goes on the wire.
 *  A blank county or line 2 is omitted rather than sent as "": PMS 400s an
 *  empty string. */
export function trimAddress(a: AddressValue): AddressValue {
  const county = a.county?.trim();
  const addressLine2 = a.addressLine2?.trim();
  return {
    street: a.street.trim(),
    ...(addressLine2 ? { addressLine2 } : {}),
    city: a.city.trim(),
    state: a.state.trim(),
    zip: a.zip.trim(),
    ...(county ? { county } : {}),
  };
}

/** The four parts alone, for an editor that doesn't carry a county. */
export function withoutCounty(a: AddressValue): AddressValue {
  return { street: a.street, city: a.city, state: a.state, zip: a.zip };
}

// Google Places names a county the way the map labels it ("Cook County",
// "Orleans Parish", "Juneau City and Borough"). PMS stores the bare name, which
// is also how the ACORD forms and Great American's application carry it ("Cook"),
// so the suffix goes. The field stays editable for the rare name the bare form
// makes ambiguous (St. Louis County beside the independent city of St. Louis).
const COUNTY_SUFFIX =
  / (County|Parish|Borough|City and Borough|Census Area|Municipality)$/i;

/** A county name as PMS stores it: trimmed, without its "County"-style suffix. */
export function normalizeCounty(raw: string): string {
  return raw.trim().replace(COUNTY_SUFFIX, "").trim();
}

/** Who put the county there: Places or the stored address ("auto") — it belongs
 *  to that address — or the broker ("typed"). */
export type CountySource = "auto" | "typed";

/**
 * One hand edit to one part of an address (BRK-S308). A county that came with
 * the address belongs to it, so editing the street, city or state by hand
 * clears it: a different address must not keep the old county (the rule
 * `addressExtras` applied while the county was carried unseen). A county the
 * broker typed is theirs and stays.
 */
export function editAddressPart(
  value: AddressValue,
  part: "street" | "city" | "state" | "zip",
  next: string,
  countySource: CountySource,
): AddressValue {
  const edited = { ...value, [part]: next };
  const moved = part !== "zip" && next !== value[part];
  if (moved && countySource === "auto" && value.county) {
    return { ...edited, county: "" };
  }
  return edited;
}

/**
 * The parts a one-line address DISPLAY needs. Deliberately wider than
 * `AddressValue`, which is what this portal's inputs are controlled by: a
 * stored or ACORD-derived address may carry a second line the portal never asks
 * for, and dropping it on display would quietly lose a suite number an
 * underwriter needs.
 */
export interface AddressParts {
  street: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  zip: string;
}

/**
 * One-line display form, e.g. "1 Via Sonoma, Suite 210, La Jolla, CA 92037".
 *
 * The ONE definition of an address line in this portal (BRK-S106) — the account
 * card, the brokerage card and the submitted bond order all read through it, so
 * the same address cannot render three ways. Returns an em dash when there is
 * no address, so callers don't each repeat the null check.
 *
 * Character-for-character the output of PMS's `formatAddress`, deliberately: a
 * broker and an underwriter reading the same bond order must see the same
 * string. Reimplemented rather than imported — the two repos share no code — so
 * the parity is pinned by `test/lib/bondValueDisplay.test.ts`, not by a
 * dependency.
 */
export function formatAddress(parts: AddressParts | null | undefined): string {
  if (!parts) return "—";
  const line2 = parts.addressLine2 ? `, ${parts.addressLine2}` : "";
  return `${parts.street}${line2}, ${parts.city}, ${parts.state} ${parts.zip}`;
}
